import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useAllData, getDimension } from '../data/loaders';
import GlasoviRail from '../components/GlasoviRail';
import AxisBadge from '../components/AxisBadge';
import { dimensionPath } from '../lib/routing';

// Hand-curated top findings (the 6–8 "Najvažnije" punchlines).
// Each links to the L3 dimension page where it's explored.
const TOP_FINDINGS: Array<{ text: string; codes: string[] }> = [
  {
    text: 'Glasanje van biračkog mesta je najslabija tačka — tu se dešava najviše prevara.',
    codes: ['OP-09', 'SN-P5'],
  },
  {
    text: '"Oboriće nam BM" je glavna poluga kojom nas gase — mirno insistirati, ne se povući.',
    codes: ['OP-11'],
  },
  {
    text: 'Dijagonalni raspored paravana se pokazao kao najefikasnija fizička mera.',
    codes: ['OP-08'],
  },
  {
    text: 'Kontrolor je prava uloga — posmatrač bez kontrolora ima malo stvarnog uticaja.',
    codes: ['OP-02'],
  },
  {
    text: 'Fantom-misije su plaćena vojska za zastrašivanje — procena ~€100k u jednoj opštini.',
    codes: ['SN-P3'],
  },
  {
    text: 'Telefoni na BM omogućavaju realno-vremensku kapilarnu koordinaciju — zabraniti ih.',
    codes: ['SN-P7'],
  },
  {
    text: 'Sela traže drugu strategiju — dnevne mere ne menjaju kupljeno selo.',
    codes: ['ST-02'],
  },
  {
    text: 'Pre-izbornu evidenciju biračkog spiska treba ozbiljno ojačati.',
    codes: ['ST-03'],
  },
];

function LandingContent() {
  const data = useAllData();
  const { summary, tagFrequency } = data.stats;
  // Exclude incident (I*) tags; they're not analytical dimensions.
  const top5 = tagFrequency.filter((t) => !t.code.startsWith('I')).slice(0, 5);

  return (
    <>
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pt-12 pb-8">
        <h1 className="font-serif text-4xl leading-tight text-ink sm:text-5xl">
          Šta su posmatrači rekli
        </h1>
        <p className="mt-4 text-lg text-neutral-600">
          {summary.totalResponses} odgovora · {summary.opstine.length} opštine ·{' '}
          {summary.totalQuotes} citata · iskustvo posmatrača studentske liste
        </p>
      </section>

      {/* Two big axis cards */}
      <section className="mx-auto grid max-w-6xl gap-6 px-6 md:grid-cols-2">
        <Link
          to="/operativa"
          className="group block rounded-lg bg-axis-op p-8 text-white transition-transform hover:-translate-y-0.5"
        >
          <div className="font-mono text-xs uppercase tracking-widest opacity-80">Naša operativa</div>
          <div className="mt-2 font-serif text-3xl leading-tight">
            14 stvari koje mi možemo da promenimo do sledećih izbora.
          </div>
          <div className="mt-6 text-sm opacity-90">
            → Istraži 14 pitanja
          </div>
        </Link>
        <Link
          to="/taktike-protivnika"
          className="group block rounded-lg bg-axis-sn p-8 text-white transition-transform hover:-translate-y-0.5"
        >
          <div className="font-mono text-xs uppercase tracking-widest opacity-80">Taktike protivnika</div>
          <div className="mt-2 font-serif text-3xl leading-tight">
            12 obrazaca koje smo uočili kod SNS-a i fantom-misija.
          </div>
          <div className="mt-6 text-sm opacity-90">
            → Istraži 12 obrazaca
          </div>
        </Link>
      </section>

      {/* Top findings */}
      <section className="mx-auto max-w-6xl px-6 pt-16">
        <h2 className="font-serif text-2xl text-ink">Najvažnije</h2>
        <ol className="mt-6 space-y-4">
          {TOP_FINDINGS.map((f, i) => (
            <li key={i} className="flex items-start gap-4 rounded-md bg-white p-4 shadow-sm">
              <span className="font-mono text-sm text-neutral-400">{String(i + 1).padStart(2, '0')}</span>
              <div className="flex-1">
                <p className="text-ink leading-relaxed">{f.text}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {f.codes.map((c) => (
                    <Link key={c} to={dimensionPath(c)}>
                      <AxisBadge code={c} />
                    </Link>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Coverage heatmap + leaderboard */}
      <section className="mx-auto max-w-6xl px-6 pt-16">
        <h2 className="font-serif text-2xl text-ink">Pokrivenost tema po opštinama</h2>
        <p className="mt-2 text-sm text-neutral-600">
          Koja osovina dominira u odgovorima iz svake opštine.
        </p>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="py-2 pr-4 text-left font-medium text-neutral-500">Opština</th>
                <th className="py-2 px-2 text-center font-medium text-axis-op-ink">Naša operativa</th>
                <th className="py-2 px-2 text-center font-medium text-axis-sn-ink">Taktike protivnika</th>
                <th className="py-2 px-2 text-center font-medium text-axis-st-ink">Strategija</th>
                <th className="py-2 px-2 text-center font-medium text-axis-mo-ink">Atmosfera</th>
              </tr>
            </thead>
            <tbody>
              {data.stats.opstinaCoverage.map((row) => {
                const max = Math.max(row.op, row.sn, row.st, row.mo, 1);
                return (
                  <tr key={row.opstina} className="border-b border-neutral-100">
                    <td className="py-2 pr-4 font-mono text-neutral-700">{row.opstina}</td>
                    {(['op', 'sn', 'st', 'mo'] as const).map((ax) => {
                      const v = row[ax];
                      const intensity = v / max;
                      return (
                        <td key={ax} className="py-2 px-2 text-center">
                          <span
                            className={`inline-flex h-7 w-7 items-center justify-center rounded font-mono text-xs ${
                              v === 0
                                ? 'bg-neutral-100 text-neutral-400'
                                : `bg-axis-${ax} text-white`
                            }`}
                            style={v > 0 ? { opacity: 0.4 + intensity * 0.6 } : undefined}
                            title={`${v} odgovora`}
                          >
                            {v}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-6">
          <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">
            Top 5 najpominjanije teme:
          </span>
          <ol className="mt-3 flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:gap-3">
            {top5.map((t) => {
              const dim = getDimension(data, t.code);
              return (
                <li key={t.code}>
                  <Link
                    to={dimensionPath(t.code)}
                    className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 shadow-sm hover:shadow-md"
                  >
                    <AxisBadge code={t.code} />
                    <span className="text-ink">{dim?.shortTitle ?? t.code}</span>
                    <span className="font-mono text-xs text-neutral-500">({t.count})</span>
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Veća pitanja */}
      <section className="mx-auto max-w-6xl px-6 pt-16">
        <h2 className="font-serif text-2xl text-ink">Veća pitanja</h2>
        <p className="mt-2 text-sm text-neutral-600">
          Pitanja koja prevazilaze izborni dan — za strategije.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.dimensions
            .filter((d) => d.axis === 'st')
            .map((d) => (
              <Link
                key={d.code}
                to={dimensionPath(d.code)}
                className="block rounded-md border-l-4 border-axis-st bg-white p-4 transition-shadow hover:shadow-md"
              >
                <AxisBadge code={d.code} />
                <div className="mt-2 font-medium text-ink">{d.shortTitle}</div>
                <div className="mt-2 text-xs leading-relaxed text-neutral-600 line-clamp-3">
                  {d.definition}
                </div>
              </Link>
            ))}
        </div>
      </section>

      <GlasoviRail />
    </>
  );
}

function Fallback() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-24 text-center text-neutral-500">
      <p className="font-serif italic">Učitava se analiza…</p>
    </div>
  );
}

export default function Landing() {
  return (
    <Suspense fallback={<Fallback />}>
      <LandingContent />
    </Suspense>
  );
}
